<template>
  <el-dialog
      :visible.sync="dialogVisible"
      width="780px"
      top="5vh"
      append-to-body
      :close-on-click-modal="false"
      custom-class="evidence-detail-dialog"
  >
    <div slot="title" class="evidence-detail-dialog__header">
      <div class="evidence-detail-dialog__header-icon">
        <i class="el-icon-document"></i>
      </div>
      <div class="evidence-detail-dialog__header-text">
        <div class="evidence-detail-dialog__header-title">存证详情</div>
        <div v-if="form.achievementName" class="evidence-detail-dialog__header-sub">
          {{ form.achievementName }}
        </div>
      </div>
    </div>

    <div class="evidence-detail-dialog__body">
      <section class="detail-section">
        <div class="detail-section__title">
          <span class="detail-section__dot"></span>
          基本信息
        </div>
        <el-row :gutter="16">
          <el-col :span="12">
            <div class="detail-field">
              <span class="detail-field__label">成果名称</span>
              <span class="detail-field__value">{{ displayValue(form.achievementName) }}</span>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="detail-field">
              <span class="detail-field__label">完成时间</span>
              <span class="detail-field__value">{{ displayValue(form.completionTime) }}</span>
            </div>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <div class="detail-field">
              <span class="detail-field__label">持有单位/人</span>
              <span class="detail-field__value">{{ displayValue(form.holdingUnit) }}</span>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="detail-field">
              <span class="detail-field__label">完成单位/人</span>
              <span class="detail-field__value">{{ displayValue(form.completionUnit) }}</span>
            </div>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <div class="detail-field">
              <span class="detail-field__label">持有单位/人联系电话</span>
              <span class="detail-field__value">{{ displayValue(form.phoneNumber) }}</span>
            </div>
          </el-col>
        </el-row>
      </section>

      <section class="detail-section">
        <div class="detail-section__title">
          <span class="detail-section__dot"></span>
          分类与评估
        </div>
        <div class="detail-field">
          <span class="detail-field__label">技术领域</span>
          <div class="detail-tags">
            <el-tag v-if="form.technicalField" size="small" type="primary">{{ form.technicalField }}</el-tag>
            <span v-else class="detail-field__value detail-field__value--empty">—</span>
          </div>
        </div>
        <div class="detail-eval-grid">
          <div class="detail-eval-item">
            <div class="detail-eval-item__label">自我评估</div>
            <div class="detail-eval-item__value">
              {{ displayEval(form.selfValuation) }}
              <span class="detail-eval-item__unit">万元</span>
            </div>
          </div>
          <div class="detail-eval-item">
            <div class="detail-eval-item__label">专家评估</div>
            <div class="detail-eval-item__value">
              {{ displayEval(form.expertValuation) }}
              <span class="detail-eval-item__unit">万元</span>
            </div>
          </div>
          <div class="detail-eval-item">
            <div class="detail-eval-item__label">综合评估</div>
            <div class="detail-eval-item__value">
              {{ displayEval(form.comprehensiveEvaluation) }}
              <span class="detail-eval-item__unit">万元</span>
            </div>
          </div>
        </div>
      </section>

      <section class="detail-section">
        <div class="detail-section__title">
          <span class="detail-section__dot"></span>
          转化方式
        </div>
        <div class="detail-tags">
          <el-tag
              v-for="item in conversionMethods"
              :key="item"
              size="small"
              type="info"
          >
            {{ item }}
          </el-tag>
          <span v-if="!conversionMethods.length" class="detail-field__value detail-field__value--empty">—</span>
        </div>
      </section>

      <section class="detail-section">
        <div class="detail-section__title">
          <span class="detail-section__dot"></span>
          成果描述
        </div>
        <div class="detail-text-block">
          <span class="detail-field__label">成果介绍</span>
          <div class="detail-text-block__content">{{ displayValue(form.introductionAchievements) }}</div>
        </div>
        <div class="detail-text-block">
          <span class="detail-field__label">核心技术</span>
          <div class="detail-text-block__content">{{ displayValue(form.coreTechnology) }}</div>
        </div>
        <div class="detail-text-block">
          <span class="detail-field__label">场景应用</span>
          <div class="detail-text-block__content">{{ displayValue(form.scenarioApplication) }}</div>
        </div>
        <div class="detail-text-block">
          <span class="detail-field__label">市场前景</span>
          <div class="detail-text-block__content">{{ displayValue(form.marketProspect) }}</div>
        </div>
      </section>

      <section class="detail-section detail-section--materials">
        <div class="detail-section__title">
          <span class="detail-section__dot"></span>
          附件材料
        </div>
        <div class="detail-field">
          <span class="detail-field__label">成果评价材料</span>
          <el-link
              v-if="form.uploadFiles"
              type="primary"
              :underline="false"
              class="detail-file-link"
              @click="openFile(form.uploadFiles)"
          >
            <i class="el-icon-document"></i>
            {{ getFileLabel(form.uploadFiles) }}
          </el-link>
          <span v-else class="detail-field__value detail-field__value--empty">—</span>
        </div>
        <div class="detail-field">
          <span class="detail-field__label">自我评估证明材料</span>
          <el-link
              v-if="form.evaluateSupportingMaterials"
              type="primary"
              :underline="false"
              class="detail-file-link"
              @click="openFile(form.evaluateSupportingMaterials)"
          >
            <i class="el-icon-document"></i>
            {{ getFileLabel(form.evaluateSupportingMaterials) }}
          </el-link>
          <span v-else class="detail-field__value detail-field__value--empty">—</span>
        </div>
        <div class="detail-field">
          <span class="detail-field__label">成果展示图片</span>
          <div v-if="form.uploadImg" class="detail-image-box">
            <img
                :src="getFileUrl(form.uploadImg)"
                alt="成果展示图片"
                @click="openFile(form.uploadImg)"
            />
          </div>
          <span v-else class="detail-field__value detail-field__value--empty">—</span>
        </div>
      </section>
    </div>

    <div slot="footer" class="evidence-detail-dialog__footer">
      <el-button type="primary" @click="dialogVisible = false">关 闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { openUploadedFile, resolveFileUrl, getFileBaseName } from "@/config/upload";

export default {
  name: "EvidenceDetailDialog",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    form: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      }
    },
    conversionMethods() {
      const value = this.form.conversionMethod;
      if (Array.isArray(value)) {
        return value.filter(Boolean);
      }
      if (typeof value === "string" && value) {
        return value.split(",").filter(Boolean);
      }
      return [];
    }
  },
  methods: {
    displayValue(val) {
      if (val === null || val === undefined || val === "") {
        return "—";
      }
      return val;
    },
    displayEval(val) {
      if (val === null || val === undefined || val === "") {
        return "—";
      }
      return val;
    },
    getFileUrl(filename) {
      return resolveFileUrl(filename);
    },
    getFileLabel(path) {
      return getFileBaseName(path) || path || "—";
    },
    openFile(path) {
      openUploadedFile(path);
    }
  }
};
</script>

<style lang="scss">
@import "@/assets/css/evidence-detail-dialog.scss";
</style>
